import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { Tabs, Tab } from '@mui/material'
import Carousel from '@/modules/base/components/carousel/carousel'
import DocumentDrawerPreview from './document-drawer-preview'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { ToggleSlider, SelectInput } from '@/modules/base/components/inputs'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconSignaturePen from '@assets/images/icons/signature_pen.svg'
// FLAG:useDocuAi — artifact preview-variant switcher (original/XML/report/validation report/attachments).
import orgamax from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { buildInvoicePreviewDocuments, resolvePreselectedDocumentId, InvoicePreviewSource, InvoicePreviewDocument } from '@/modules/document/utils/invoice-preview-documents'

// FLAG:useDocuAi — how far the switcher container is inset from the preview pane's right edge, and
// the gap the preview title row keeps between its own content and it. Used by BOTH the style below
// and the reservation the title row is given, so the two cannot drift apart.
const TABS_RIGHT_OFFSET = 32
const TITLE_GAP = 20

const useStyles = makeStyles<ThemeInterface, { fitToPane?: boolean, useDocuAiEnabled?: boolean }>((theme: ThemeInterface) => ({
  // FLAG:useDocuAi — flag-ON the preview fills the actual (resizable) pane so the document
  // scales to fit it; flag-OFF keeps the baseline fixed calc(100vw - defaultDrawerWidth).
  carouselWrapper: ({ fitToPane }) => ({
    height: '100%',
    width: fitToPane ? '100%' : `calc(100vw - ${theme.defaultDrawerWidth}px)`,
    overflow: 'hidden', // Prevent horizontal overflow from carousel
    position: 'relative',
  }),
  carouselContent: ({ fitToPane }) => ({
    height: '100%',
    width: '100%',
    position: 'relative',
    '& .swiper-slide': {
      height: '100%',
      width: fitToPane ? '100%' : `calc(100vw - ${theme.defaultDrawerWidth})px !important`,
    },
    '& .swiper-wrapper': {
      height: '100%',
    },
    '& .swiper': {
      height: '100%',
    },
  }),
  // FLAG:useDocuAi — flag-ON the switcher is anchored INSIDE the preview pane instead of at a
  // fixed viewport offset. `right: 540` was tuned for the flag-OFF, content-sized details panel;
  // the useDocuAi panel opens at 720px (DEFAULT_DETAILS_WIDTH) and is user-resizable 510–1100,
  // and it paints at zIndex 1100 over a solid white background — so at 540 the panel covered the
  // dropdown's right half, and any drag moved the clipping point. The preview pane is the exact
  // area left of the panel and is already `position: relative`, so anchoring to it tracks every
  // width automatically. Flag-OFF keeps the verbatim baseline (fixed / right 540).
  tabsContainer: ({ useDocuAiEnabled }) => ({
    position: useDocuAiEnabled ? 'absolute' : 'fixed',
    top: 30,
    right: useDocuAiEnabled ? TABS_RIGHT_OFFSET : 540,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    height: 38,
  }),
  signatureToggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingRight: 12,
    marginTop: 4,
  },
  signatureToggleLabel: {
    fontSize: 14,
    color: theme.colors.black,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  signatureToggleIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  // FLAG:useDocuAi — variant dropdown container styled to match the development tab-pill
  // aesthetic (white pill, subtle border + shadow) so the switcher reads as one clean control
  // instead of a bare select floating over the preview.
  selectorContainer: {
    display: 'flex',
    alignItems: 'center',
    height: 38,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadiusLarge,
    border: `1px solid ${theme.borderColors.default}`,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    // The react-select menu is rendered below this element. Clipping the pill to
    // its 38px header height also clipped every artifact option, leaving only
    // the selected "Original" value visible even though the options existed.
    overflow: 'visible',
  },
  documentSelect: {
    width: 340,
  },
  // FLAG:useDocuAi — soft fade on variant switch so the dropdown-driven swap reads as a gentle
  // transition instead of a hard remount flash. Only the flag-ON single-preview branch uses it.
  previewFade: {
    animation: '$previewFadeIn 180ms ease',
  },
  // @keyframes is not a class; the plugin cannot see it referenced from the `animation:` shorthand.
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@keyframes previewFadeIn': {
    from: { opacity: 0.3 },
    to: { opacity: 1 },
  },
}))

interface GeneratedFile {
  id: string
  decodedName: string
  mimetype: string
  url: string
  createdAt: string
  minioId?: string
}

interface DocumentWithGeneratedFiles {
  id: string
  name: string
  decodedName: string
  mimetype: string
  url: string
  generatedFiles?: GeneratedFile[]
  [key: string]: unknown
}

interface DocumentCarouselWrapperProps {
  document: DocumentWithGeneratedFiles
  // FLAG:useDocuAi — fill the resizable pane (fit-to-width) instead of the fixed calc width.
  fitToPane?: boolean
  // FLAG:useDocuAi — search parent-rollup: DMS version id of the matched child to
  // preselect in the switcher when the drawer was opened from a child-faced row.
  preselectChildVersionId?: string | null
}

const DocumentCarouselWrapper: React.FC<DocumentCarouselWrapperProps> = ({ document, fitToPane = false, preselectChildVersionId = null }) => {
  const useDocuAiEnabled = orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const classes = useStyles({ fitToPane, useDocuAiEnabled })
  const [activeTab, setActiveTab] = useState(0)
  const [swiperInstance, setSwiperInstance] = useState(null)
  const [showSignature, setShowSignature] = useState(false)
  // FLAG:useDocuAi — the dropdown selects by document id (single preview), not a swiper index.
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null)

  const theme = useTheme() as ThemeInterface

  useEffect(() => {
    setActiveTab(0)
    setShowSignature(false)
    setActiveDocumentId(null)
    if (swiperInstance) {
      swiperInstance.slideTo(0)
    }
  }, [document.id])

  const { displayDocuments, signatureDocument } = useMemo(() => {
    // FLAG:useDocuAi — build the full artifact set (original / XML / report / validation report / attachments)
    // from generatedFiles + DMS invoiceRelatedFiles + validationResult. flag-OFF keeps the verbatim
    // development logic below ([document, ...generatedFiles]).
    if (useDocuAiEnabled) {
      return buildInvoicePreviewDocuments(document as unknown as InvoicePreviewSource)
    }
    const docs = [document]
    let signature = null

    if (document?.generatedFiles?.length) {
      const sortedGeneratedFiles = [...document.generatedFiles].sort((a, b) => {
        const getOrder = (file: GeneratedFile) => {
          const name = file.decodedName?.toLowerCase() || ''
          if (name.includes('report')) return 0
          if (name.includes('xml')) return 1
          if (name.includes('_signed')) return 999 // Will be filtered out
          return 3
        }
        return getOrder(a) - getOrder(b)
      })

      signature = sortedGeneratedFiles.find(f =>
        f.decodedName?.toLowerCase().includes('_signed'),
      )

      const nonSignatureFiles = sortedGeneratedFiles.filter(f =>
        !f.decodedName?.toLowerCase().includes('_signed'),
      )

      const generatedDocs = nonSignatureFiles.map(file => ({
        id: file.id,
        name: file.decodedName,
        decodedName: file.decodedName,
        mimetype: file.mimetype,
        url: file.url,
        minioId: file.minioId,
        isGeneratedFile: true,
        metaData: {
          zoom: file.mimetype === 'application/pdf' ? 2 : 1,
          rotate: 0,
        },
        tags: [],
        tenantId: document.tenantId,
      }))
      docs.push(...generatedDocs)
    }

    return { displayDocuments: docs, signatureDocument: signature }
  }, [document.id, document.generatedFiles, useDocuAiEnabled,
    (document as { invoiceRelatedFiles?: unknown }).invoiceRelatedFiles,
    (document as { validationResult?: unknown }).validationResult])

  // FLAG:useDocuAi — search parent-rollup: preselect the matched child's variant once
  // it is present in the built document set. Runs only while no variant was chosen
  // yet (activeDocumentId null — the per-document reset above re-arms it); paging and
  // plain opens clear preselectChildVersionId in the drawer state, and an unresolved
  // child (relation deleted) resolves to null → Original stays, as today.
  useEffect(() => {
    if (!useDocuAiEnabled || !preselectChildVersionId || activeDocumentId) return
    const preselectedId = resolvePreselectedDocumentId(displayDocuments as Array<Partial<InvoicePreviewDocument>>, preselectChildVersionId)
    if (preselectedId) setActiveDocumentId(preselectedId)
  }, [useDocuAiEnabled, preselectChildVersionId, displayDocuments, document.id, activeDocumentId])

  // FLAG:useDocuAi — dropdown options + the currently-selected variant (single preview).
  const documentOptions = useMemo(
    () => displayDocuments.map(doc => ({ label: (doc as InvoicePreviewDocument).displayLabel || doc.name, value: doc.id })),
    [displayDocuments],
  )
  const activeDocument = (useDocuAiEnabled
    && (displayDocuments.find(doc => doc.id === activeDocumentId) || displayDocuments[0]))
    || displayDocuments[0]
  const activeIndex = Math.max(0, displayDocuments.findIndex(doc => doc.id === activeDocument?.id))

  const tabLabels = useMemo(() => {
    const labels = []
    displayDocuments.forEach((doc, i) => {
      if (!i) labels.push(lang.documents.previewCarouselWrapperTabs.original)
      else if (doc.name?.toLowerCase().includes('xml')) labels.push(lang.documents.previewCarouselWrapperTabs.xml)
      else if (doc.name?.toLowerCase().includes('report')) labels.push(lang.documents.previewCarouselWrapperTabs.report)
    })
    return labels
  }, [displayDocuments])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    if (swiperInstance) {
      swiperInstance.slideTo(newValue)
    }
  }

  const handleSlideChange = (swiper: { activeIndex: number }) => {
    setActiveTab(swiper.activeIndex)
  }

  const shouldShowToggle = signatureDocument && (displayDocuments.length === 1 || activeTab === 0)

  // FLAG:useDocuAi — this container floats OVER the preview's title row (top 30 + height 38 sits
  // inside its 87px), and that row carries the file name, the "E-Rechnung gültig" verdict and the
  // status badge. Same defect as in invoice-artifact-preview (QA, 2026-08-10): a long e-invoice name
  // ran underneath it and the badge was covered. Unlike there, the width is NOT a constant — the
  // container holds the 340px switcher and, on the Original variant, the signature toggle whose
  // label is translated text. So it is measured instead of computed; the row is told the result and
  // ends its own content before it.
  const tabsRef = useRef<HTMLDivElement>(null)
  const [tabsWidth, setTabsWidth] = useState(0)
  useLayoutEffect(() => {
    setTabsWidth(tabsRef.current?.getBoundingClientRect().width || 0)
    // Re-measure whenever the container's CONTENT can change — its children are the only thing that
    // moves its width (it is right-anchored at a fixed offset).
  }, [useDocuAiEnabled, displayDocuments.length, shouldShowToggle, activeDocument?.id])
  // Flag-OFF the container is `position: fixed` at the baseline offset and the row must stay
  // byte-identical, so the reservation is flag-ON only. TABS_RIGHT_OFFSET mirrors `tabsContainer`'s
  // own `right`, TITLE_GAP the title row's gap.
  const titleInsetRight = useDocuAiEnabled && tabsWidth
    ? tabsWidth + TABS_RIGHT_OFFSET + TITLE_GAP
    : 0

  return (
    <div className={classes.carouselWrapper}>
      <div className={classes.carouselContent}>
        {(displayDocuments.length > 1 || shouldShowToggle) && (
          <div className={classes.tabsContainer} ref={tabsRef}>
            {shouldShowToggle && (
              <div className={classes.signatureToggleContainer}>
                <div className={classes.signatureToggleIcon}><SVGIcon svg={IconSignaturePen} width="20px" height="20px" /></div>
                <span className={classes.signatureToggleLabel}>{lang.documents.showSignatureLabel}</span>
                <ToggleSlider checked={showSignature} onChange={setShowSignature} useCheckedAsValue={true} dataQsId="signature-toggle" />
              </div>
            )}
            {/* eslint-disable-next-line multiline-ternary */}
            {displayDocuments.length > 1 && (useDocuAiEnabled ? (
              /* FLAG:useDocuAi — variant dropdown (original/XML/report/validation report/attachments). */
              <div className={classes.selectorContainer}>
                <SelectInput
                  dataQsId="document-preview-select"
                  name="document-preview-select"
                  className={classes.documentSelect}
                  value={activeDocument?.id || ''}
                  options={documentOptions}
                  allowCreate={false}
                  notAsync
                  disableSearch={displayDocuments.length < 8}
                  useFormik={false}
                  handleChange={(option) => setActiveDocumentId(option.value)}
                  customSelectOptions={{
                    placeholder: '',
                    labelKey: 'label',
                    valueKey: 'value',
                    clearable: false,
                    backspaceRemoves: false,
                    // No menuWidth: the open menu is left-aligned with the 340px control, so a
                    // forced 420 overhung 80px past it — and now that the switcher is anchored
                    // 32px inside the preview pane (which clips with overflow:hidden), that
                    // overhang was cut off. Same fix already applied in invoice-artifact-preview.
                  }}
                />
              </div>
            ) : (
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="standard"
                sx={{
                  backgroundColor: theme.colors.white,
                  borderRadius: '8px',
                  padding: '2px 0',
                  border: `1px solid ${theme.borderColors.default}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  minHeight: 'auto',
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                  '& .MuiTab-root': {
                    minHeight: 'auto',
                    minWidth: 'auto',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: theme.colors.black,
                    backgroundColor: theme.colors.white,
                    borderRadius: '6px',
                    margin: '0 2px',
                    transition: 'all 0.2s ease',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: theme.colors.white,
                      backgroundColor: theme.primary,
                    },
                    '&:hover': {
                      backgroundColor: theme.colors.grey,
                      '&.Mui-selected': {
                        backgroundColor: theme.primary,
                      },
                    },
                  },
                }}
              >
                {displayDocuments.map((doc, index) => (
                  <Tab
                    key={`${doc.id}#${index}`}
                    label={tabLabels[index] || `${index + 1}`}
                    disableRipple
                  />
                ))}
              </Tabs>
            ))}
          </div>
        )}
        {/* eslint-disable-next-line multiline-ternary */}
        {useDocuAiEnabled ? (
          /* FLAG:useDocuAi — render the single dropdown-selected variant (no swiper). */
          <div
            key={activeDocument?.id}
            className={classes.previewFade}
            style={{
              // FLAG:useDocuAi — fill the resizable pane (fitToPane) so the document aligns
              // cleanly with the panel edge; only fall back to the fixed viewport-minus-drawer
              // width when fitToPane is off (never in the flag-OFF branch, which is untouched).
              width: fitToPane ? '100%' : `calc(100vw - ${theme.defaultDrawerWidth}px)`,
              height: '100%',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DocumentDrawerPreview
              document={activeDocument}
              isCarouselSlide={displayDocuments.length > 1}
              signatureDocument={activeIndex === 0 ? signatureDocument : undefined}
              showSignature={activeIndex === 0 ? showSignature : false}
              onToggleSignature={activeIndex === 0 ? setShowSignature : undefined}
              titleInsetRight={titleInsetRight}
            />
          </div>
        ) : displayDocuments.length === 1
          ? (
            <DocumentDrawerPreview
              signatureDocument={signatureDocument}
              showSignature={showSignature}
              onToggleSignature={setShowSignature}
            />
          )
          : (
          /* @ts-expect-error - Carousel component has TypeScript definition issues but works at runtime */
            <Carousel
              infinite={false}
              draggable={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              settings={{
                onSwiper: setSwiperInstance,
                onSlideChange: handleSlideChange,
              }}
            >
              {displayDocuments.map((doc, index) => (
                <div key={doc.id || index} style={{
                  width: `calc(100vw - ${theme.defaultDrawerWidth}px)`,
                  height: '100%',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <DocumentDrawerPreview
                    document={doc}
                    isCarouselSlide={true}
                    signatureDocument={index === 0 ? signatureDocument : undefined}
                    showSignature={index === 0 ? showSignature : false}
                    onToggleSignature={index === 0 ? setShowSignature : undefined}
                  />
                </div>
              ))}
            </Carousel>
          )}
      </div>
    </div>
  )
}

export default DocumentCarouselWrapper
